import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import axios from 'axios';
import { Search, Plus, ArrowUpRight, ArrowLeft, ArrowRight, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

import { PageShell } from '@/components/brand/PageShell';
import { LoadingState, EmptyState } from '@/components/brand/States';
import { BrandButton } from '@/components/brand/Buttons';
import { LogoMark } from '@/components/brand/Logo';
import { ProductImage } from '@/components/brand/ProductImage';
import { formatPrice } from '@/lib/formatPrice';
import { capture } from '@/lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* -------------------------------------------------------------------------- */
/*  Categories — baby-specific                                                 */
/* -------------------------------------------------------------------------- */

const categories = [
  { name: 'Everything',  value: 'all' },
  { name: 'Feeding',     value: 'feeding' },
  { name: 'Sleeping',    value: 'sleeping' },
  { name: 'Diapering',   value: 'diapering' },
  { name: 'On the move', value: 'baby-gear' },
  { name: 'Health',      value: 'health-safety' },
  { name: 'Bath',        value: 'bathing' },
  { name: 'Nursery',     value: 'nursery-decor' },
  { name: 'Wear',        value: 'clothing' },
  { name: 'Play',        value: 'playing' },
];

const subcategories = {
  'feeding': [
    { name: 'Bottles & nipples',        keywords: ['feeding bottle', 'anti-colic bottle', 'peristaltic bottle', 'avent bottle', 'pigeon bottle', 'mee mee anti-colic'] },
    { name: 'Breast pumps',             keywords: ['breast pump', 'wearable pump', 'electric breast pump', 'portable electric breast'] },
    { name: 'Sterilizers & warmers',    keywords: ['sterilizer', 'bottle warmer', 'glory electric', 'hot bot bottle', 'avent sterilizer'] },
    { name: 'High chairs',              keywords: ['high chair', 'marshmallow high', 'galaxy 2 in1', 'rocking baby high'] },
    { name: 'Feeding accessories',      keywords: ['feeding pillow', 'silicone baby feeding', 'feeding essentials', 'mamajama bundle'] },
  ],
  'sleeping': [
    { name: 'Cribs & bassinets',        keywords: ['baby cot', 'baby crib', 'bassinet', 'cradle', 'bedside cot'] },
    { name: 'Swaddles & sleep sacks',   keywords: ['swaddle', 'sleep sack', 'sleeping bag', 'muslin swaddle'] },
    { name: 'Blankets & bedding',       keywords: ['baby blanket', 'bedding', 'dry sheet', 'mattress'] },
    { name: 'Bouncers & rockers',       keywords: ['bouncer', 'rocker', 'swing', 'electric swing', 'baby chair bouncer'] },
    { name: 'Sleep accessories',        keywords: ['sleepsuit', 'sleeping mat', 'sleep'] },
  ],
  'diapering': [
    { name: 'Diapers & wipes',          keywords: ['pampers', 'huggies', 'gigglebox premium', 'baby dry sheet', 'novo baby dry'] },
    { name: 'Changing mats',            keywords: ['diaper changing mat', 'babyhug premium diaper', 'waterproof bed protector'] },
    { name: 'Diaper bags',              keywords: ['diaper bag', 'caramello regal', 'motherly crown melange', 'maternity bag'] },
  ],
  'baby-gear': [
    { name: 'Strollers & prams',        keywords: ['baby stroller', 'pram', 'stroller wagon', 'cuppy stroller'] },
    { name: 'Car seats',                keywords: ['car seat', 'isofix', 'convertible car seat'] },
    { name: 'Baby carriers',            keywords: ['baby carrier', 'hip seat', 'carrier for new born', 'ergo carrier'] },
    { name: 'Other gear',               keywords: ['cloth dryer', 'air purifier', 'grooming kit', 'mosquito net'] },
  ],
  'health-safety': [
    { name: 'Baby monitors',            keywords: ['monitor'] },
    { name: 'Thermometers & care',      keywords: ['thermometer', 'health'] },
    { name: 'Grooming kits',            keywords: ['grooming', 'kit', 'nail'] },
  ],
  'bathing': [
    { name: 'Tubs',                     keywords: ['baby bath tub', 'baybee bath tub', 'foldable bathtub'] },
    { name: 'Towels',                   keywords: ['organic cotton baby towels', 'firstcry organic'] },
    { name: 'Body care',                keywords: ['baby lotion', 'baby shampoo', 'body wash', 'baby oil', 'skin care combo', 'aveeno baby', '5-in-1 baby'] },
    { name: 'Gift sets',                keywords: ['baby basket gift', 'himalaya baby basket', 'aveeno baby gift'] },
  ],
  'nursery-decor': [
    { name: 'Play gyms & mats',         keywords: ['gym', 'mat', 'play'] },
    { name: 'Storage',                  keywords: ['storage', 'organizer'] },
    { name: 'Bedding',                  keywords: ['bed', 'mosquito net'] },
  ],
  'clothing': [
    { name: 'Bodysuits & rompers',      keywords: ['bodysuit', 'romper', 'onesie'] },
    { name: 'Sleepwear',                keywords: ['sleepsuit', 'sleep'] },
    { name: 'Accessories',              keywords: ['hat', 'socks', 'shoes'] },
  ],
  'playing': [
    { name: 'Activity gyms',            keywords: ['gym', 'mat', 'activity'] },
    { name: 'Toys & games',             keywords: ['toy'] },
    { name: 'Soft toys',                keywords: ['soft toy', 'bunny', 'plush'] },
  ],
};

const PRODUCTS_PER_PAGE = 20;

/* -------------------------------------------------------------------------- */

const ExploreGifts = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token } = useAuth();

  // Deep-link target: when arriving from a specific registry's edit page,
  // we skip the "which registry?" modal and add straight to this one.
  const addToId = searchParams.get('addTo');

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showSelectRegistry, setShowSelectRegistry] = useState(false);
  const [userRegistries, setUserRegistries] = useState([]);
  const [loadingRegistries, setLoadingRegistries] = useState(false);

  // Pre-targeted registry (when ?addTo= is set)
  const [targetRegistry, setTargetRegistry] = useState(null);
  const [addedCount, setAddedCount] = useState(0);

  /* ---------- fetch the pre-targeted registry (for the banner) ---------- */
  useEffect(() => {
    if (!addToId) {
      setTargetRegistry(null);
      return;
    }
    axios
      .get(`${API}/registry/${addToId}`)
      .then((res) => setTargetRegistry(res.data))
      .catch(() => setTargetRegistry({ id: addToId, baby_name: 'your basket' }));
  }, [addToId]);

  /* ---------- url param ---------- */
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam) {
      const map = {
        Clothing: 'clothing', Sleeping: 'sleeping', Nursery: 'sleeping',
        'Baby Gear': 'baby-gear', Feeding: 'feeding', Diapering: 'diapering',
        Bathing: 'bathing', Playing: 'playing',
      };
      setSelectedCategory(map[categoryParam] || categoryParam.toLowerCase());
    }
  }, [searchParams]);

  /* ---------- fetch on category change ---------- */
  useEffect(() => {
    fetchProducts();
    setCurrentPage(1);
    setSelectedSubcategory(null);
    // eslint-disable-line
  }, [selectedCategory]); // eslint-disable-line

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const categoryParam = selectedCategory !== 'all' ? `&category=${selectedCategory}` : '';
      const response = await axios.get(`${API}/products/search?query=all&limit=200${categoryParam}`);
      setProducts(response.data.products || []);
    } catch {
      toast.error("Couldn't load the shop");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return fetchProducts();
    setLoading(true);
    try {
      const response = await axios.get(`${API}/products/search?query=${searchQuery}&limit=200`);
      setProducts(response.data.products || []);
      setCurrentPage(1);
    } catch {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  /* ---------- subcategory filtering ---------- */
  const filteredProducts = useMemo(() => {
    if (!selectedSubcategory || selectedCategory === 'all') return products;
    const subList = subcategories[selectedCategory] || [];
    const sub = subList.find((s) => s.name === selectedSubcategory);
    if (!sub) return products;
    const haystack = (p) => `${p.name || ''} ${p.description || ''}`.toLowerCase();
    return products.filter((p) => sub.keywords.some((k) => haystack(p).includes(k.toLowerCase())));
  }, [products, selectedSubcategory, selectedCategory]);

  const pageCount = Math.max(1, Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE));
  const pagedProducts = filteredProducts.slice(
    (currentPage - 1) * PRODUCTS_PER_PAGE,
    currentPage * PRODUCTS_PER_PAGE
  );

  const goToPage = (n) => {
    setCurrentPage(Math.min(Math.max(1, n), pageCount));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /* ---------- add-to-registry flow ---------- */
  const addToRegistry = (product) => {
    if (!user) {
      toast.error('Please sign in to add to a registry');
      navigate('/');
      return;
    }
    // If we came from a specific registry's edit page, skip the picker
    // and add straight there.
    if (addToId) {
      handleAddToSelectedRegistry(addToId, product);
      return;
    }
    setSelectedProduct(product);
    fetchUserRegistries();
    setShowSelectRegistry(true);
  };

  const fetchUserRegistries = async () => {
    if (!token) return;
    setLoadingRegistries(true);
    try {
      const response = await axios.get(`${API}/registry/user/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUserRegistries(response.data || []);
    } catch {
      toast.error('Could not load your registries');
    } finally {
      setLoadingRegistries(false);
    }
  };

  const handleAddToSelectedRegistry = async (registryId, productOverride) => {
    const product = productOverride || selectedProduct;
    if (!product) return;
    try {
      // Atomic single-item add — the old GET-then-PUT-whole-array approach
      // could silently revert a guest's reservation happening at the same
      // moment. The server $pushes exactly one item now.
      const newItem = {
        name: product.name,
        description: product.description || '',
        price: product.price,
        currency_code: product.currency_code || product.currency || 'INR',
        image_url: product.image || '',
        category: product.category || '',
        url: product.affiliate_link,
      };
      await axios.post(
        `${API}/registry/${registryId}/items/add`,
        newItem,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Added to your basket');
      capture('item_added', { source: 'explore' });
      setAddedCount((c) => c + 1);
      setShowSelectRegistry(false);
      setSelectedProduct(null);
    } catch {
      toast.error('Could not add to registry');
    }
  };

  /* ---------- view ---------- */
  return (
    <PageShell stickyMobileCTA>
      {/* Add-to-registry banner — shown when arriving via ?addTo= */}
      {addToId && targetRegistry && (
        <div className="bg-clay-700 text-cream-50 border-b border-clay-800/30">
          <div className="container-page py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-[14px]">
            <div className="flex items-center gap-2.5">
              <Plus className="h-4 w-4 shrink-0" />
              <span>
                Adding to{' '}
                <span className="font-medium">
                  {targetRegistry.baby_name || targetRegistry.title || 'your basket'}
                </span>
                {addedCount > 0 && (
                  <span className="ml-2 text-cream-200">
                    · {addedCount} added so far
                  </span>
                )}
              </span>
            </div>
            <button
              onClick={() => navigate(`/registry/${addToId}/edit`)}
              className="inline-flex items-center gap-1.5 text-cream-100 hover:text-cream-50 font-medium whitespace-nowrap"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to your basket
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <section className="relative pt-16 sm:pt-24 pb-10">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-12 -right-32 h-[420px] w-[420px] rounded-full blur-3xl opacity-50"
          style={{ background: 'radial-gradient(closest-side, #F4D6CD 0%, transparent 70%)' }}
        />
        <div className="container-page relative">
          <div className="max-w-2xl">
            <div className="eyebrow mb-3">The shop</div>
            <h1 className="font-display text-display-xl text-ink leading-[1.05]">
              A curated selection,{' '}
              <span className="italic text-clay-700">for the moments ahead.</span>
            </h1>
            <p className="mt-4 text-lg text-ink-soft max-w-xl leading-relaxed">
              {products.length > 0 && (
                <span className="text-ink font-medium">{products.length}+ thoughtful picks · </span>
              )}
              From the gentlest essentials to the heirloom things. Add what you love to your basket.
            </p>
          </div>

          {/* Search */}
          <form onSubmit={handleSearch} className="mt-8 max-w-xl">
            <div className="relative">
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-muted" />
              <Input
                type="text"
                placeholder="Search the shop"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-12 pl-12 pr-28 text-[14.5px] rounded-full bg-cream-50 border border-border placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-clay-500/40 focus:border-clay-300 shadow-soft-1"
              />
              <div className="absolute right-1.5 top-1/2 -translate-y-1/2">
                <BrandButton type="submit" size="sm">Search</BrandButton>
              </div>
            </div>
          </form>
        </div>
      </section>

      {/* Category tabs */}
      <section className="sticky top-[68px] z-10 bg-cream-50/85 backdrop-blur-md border-y border-border/60">
        <div className="container-page py-4 overflow-x-auto">
          <div className="flex items-center gap-2 min-w-max">
            {categories.map((c) => (
              <button
                key={c.value}
                onClick={() => setSelectedCategory(c.value)}
                className={`px-4 h-9 rounded-full text-[13.5px] font-medium transition-all whitespace-nowrap
                  ${selectedCategory === c.value
                    ? 'bg-ink text-cream-50'
                    : 'bg-cream-50 text-ink-soft border border-border hover:border-clay-300 hover:text-clay-700'}`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Subcategory chips */}
      {selectedCategory !== 'all' && subcategories[selectedCategory]?.length > 0 && (
        <section className="border-b border-border/60 bg-cream-100/40">
          <div className="container-page py-3 overflow-x-auto">
            <div className="flex items-center gap-2 min-w-max">
              <button
                onClick={() => { setSelectedSubcategory(null); setCurrentPage(1); }}
                className={`px-3 h-8 rounded-full text-[12.5px] transition-all whitespace-nowrap
                  ${!selectedSubcategory
                    ? 'bg-clay-100 text-clay-700 border border-clay-200'
                    : 'bg-cream-50 text-ink-muted border border-border hover:border-clay-300 hover:text-clay-700'}`}
              >
                All in {categories.find((c) => c.value === selectedCategory)?.name}
              </button>
              {subcategories[selectedCategory].map((s) => (
                <button
                  key={s.name}
                  onClick={() => { setSelectedSubcategory(s.name); setCurrentPage(1); }}
                  className={`px-3 h-8 rounded-full text-[12.5px] transition-all whitespace-nowrap
                    ${selectedSubcategory === s.name
                      ? 'bg-clay-100 text-clay-700 border border-clay-200'
                      : 'bg-cream-50 text-ink-muted border border-border hover:border-clay-300 hover:text-clay-700'}`}
                >
                  {s.name}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Product grid */}
      <section className="container-page py-12 sm:py-16">
        {loading ? (
          <LoadingState label="Gathering the shop…" compact />
        ) : pagedProducts.length === 0 ? (
          <EmptyState
            eyebrow="No matches"
            title="Nothing here yet."
            body="Try a different category, clear the search, or adjust your filters."
            action={
              <BrandButton variant="outline" onClick={() => { setSelectedCategory('all'); setSelectedSubcategory(null); setSearchQuery(''); fetchProducts(); }}>
                Reset filters
              </BrandButton>
            }
          />
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 lg:gap-6">
              {pagedProducts.map((p) => (
                <article key={p.id} className="group flex flex-col rounded-2xl bg-cream-50 border border-border/70 overflow-hidden shadow-soft-1 hover:shadow-soft-2 transition-shadow">
                  <div className="relative aspect-square bg-cream-100 overflow-hidden">
                    <ProductImage src={p.image} alt={p.name} fallbackLabel={p.brand || p.name} />
                    {p.brand && (
                      <span className="absolute top-3 left-3 inline-block text-[10.5px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full bg-cream-50/90 backdrop-blur-sm text-clay-700 border border-cream-300">
                        {p.brand}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 p-4 flex flex-col">
                    <h3 className="font-display text-[15px] text-ink leading-snug line-clamp-2">{p.name}</h3>
                    {p.price ? (
                      <p className="mt-2 font-display text-base text-ink">{formatPrice(p.price, p.currency_code || p.currency)}</p>
                    ) : null}
                    <div className="mt-auto pt-4 flex gap-1.5">
                      <BrandButton
                        variant="outline"
                        size="sm"
                        onClick={() => p.affiliate_link && window.open(p.affiliate_link, '_blank', 'noopener,noreferrer')}
                        className="flex-1"
                      >
                        <ArrowUpRight className="h-3 w-3" /> View
                      </BrandButton>
                      <BrandButton
                        size="sm"
                        onClick={() => addToRegistry(p)}
                        className="flex-1"
                      >
                        <Plus className="h-3 w-3" /> Add
                      </BrandButton>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {/* Pagination */}
            {pageCount > 1 && (
              <nav className="mt-12 flex items-center justify-center gap-2 flex-wrap">
                <BrandButton
                  variant="ghost"
                  size="sm"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ArrowLeft className="h-3.5 w-3.5" /> Prev
                </BrandButton>
                {Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    onClick={() => goToPage(n)}
                    className={`min-w-9 h-9 px-3 rounded-full text-sm font-medium transition-colors
                      ${n === currentPage
                        ? 'bg-clay-600 text-cream-50'
                        : 'bg-cream-50 text-ink-soft border border-border hover:border-clay-300 hover:text-clay-700'}`}
                  >
                    {n}
                  </button>
                ))}
                <BrandButton
                  variant="ghost"
                  size="sm"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === pageCount}
                >
                  Next <ArrowRight className="h-3.5 w-3.5" />
                </BrandButton>
              </nav>
            )}
          </>
        )}
      </section>

      {/* Select-registry modal */}
      <Dialog open={showSelectRegistry} onOpenChange={setShowSelectRegistry}>
        <DialogContent className="sm:max-w-[480px] bg-cream-50 border-border/70 rounded-3xl p-0 overflow-hidden">
          <div className="px-7 pt-8 pb-2 text-center">
            <LogoMark size={26} className="text-clay-700 mx-auto mb-4" />
            <DialogHeader>
              <DialogTitle className="font-display text-[24px] leading-tight text-ink">
                Add to which basket?
              </DialogTitle>
            </DialogHeader>
            {selectedProduct && (
              <p className="mt-2 text-sm text-ink-soft truncate">
                <span className="italic">{selectedProduct.name}</span>
              </p>
            )}
          </div>

          <div className="px-7 pb-7 pt-4 space-y-2.5 max-h-[55vh] overflow-y-auto">
            {loadingRegistries ? (
              <LoadingState label="Looking up your baskets…" compact />
            ) : userRegistries.length === 0 ? (
              <EmptyState
                eyebrow="No baskets yet"
                title="Begin one first."
                body="You'll need a registry before you can add gifts to it."
                action={
                  <BrandButton onClick={() => { setShowSelectRegistry(false); navigate('/registry/new'); }}>
                    Begin a registry
                  </BrandButton>
                }
              />
            ) : (
              userRegistries.map((r) => (
                <button
                  key={r.id}
                  onClick={() => handleAddToSelectedRegistry(r.id)}
                  className="group w-full text-left rounded-2xl bg-cream-50 border border-border/70 hover:border-clay-200 p-4 transition-all"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="eyebrow mb-1 text-clay-700">{r.event_type || 'Registry'}</div>
                      <h4 className="font-display text-base text-ink">
                        {r.name || `${r.event_type || 'Your'} basket`}
                      </h4>
                      {r.event_date && (
                        <div className="mt-1 inline-flex items-center gap-1.5 text-xs text-ink-muted">
                          <Calendar className="h-3 w-3" /> {new Date(r.event_date).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    <Plus className="h-4 w-4 text-ink-muted group-hover:text-clay-700 transition-colors" />
                  </div>
                </button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};

export default ExploreGifts;
